import { useCallback } from 'react';
import { MetricPreviewModal } from './MetricPreviewModal';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import { metricDefinitionsApi } from '@/services/metric-definitions-api';
import type { IMetricDefinition, IMetricDefinitionVersion } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Helpers (module-scoped — S7721)
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<string, string> = {
  baseline: 'Baseline (pre-rewrite)',
  admin_update: 'Edited',
  admin_reset: 'Reset to defaults',
  admin_restore: 'Restored',
  // Backfilled live content of a never-edited metric (the catalogue default).
  // Labelled "Default" rather than "Current" so it never duplicates the
  // newest-row "Current" badge.
  current: 'Default',
};

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

/**
 * Build a full IMetricDefinition for previewing a past version: the version
 * snapshot supplies the editable prose + tiers, the current definition supplies
 * the non-editable metadata (group, icon, unit, direction, source).
 */
function versionAsDefinition(
  current: IMetricDefinition,
  v: IMetricDefinitionVersion,
): IMetricDefinition {
  return {
    ...current,
    name: v.name,
    tagline: v.tagline,
    definition: v.definition,
    howToUseTips: v.howToUseTips,
    tiers: v.tiers,
    canonicalValues: v.canonicalValues ?? undefined,
    calculation: v.calculation ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MetricVersionHistoryProps {
  /** The current (live) definition — supplies metadata for version previews. */
  definition: IMetricDefinition;
  /** Called with the now-live definition after a successful restore. */
  onRestored: (restored: IMetricDefinition) => void;
}

export function MetricVersionHistory({ definition, onRestored }: Readonly<MetricVersionHistoryProps>) {
  const metricKey = definition.metricKey;
  const load = useCallback(() => metricDefinitionsApi.getVersions(metricKey), [metricKey]);
  const onRestore = useCallback(
    async (version: number) => {
      const restored = await metricDefinitionsApi.restore(metricKey, version);
      onRestored(restored);
    },
    [metricKey, onRestored],
  );

  return (
    <VersionHistoryPanel<IMetricDefinitionVersion>
      load={load}
      onRestore={onRestore}
      sourceLabel={sourceLabel}
      renderPreview={(v, onClose) => (
        <MetricPreviewModal
          open
          onClose={onClose}
          definition={versionAsDefinition(definition, v)}
        />
      )}
    />
  );
}
